import React, { useState, useRef, useCallback, useEffect, useMemo } from 'react';
import { Controlled as CodeMirror } from 'react-codemirror2';
import { Tabs, TabList, Tab, TabPanel } from 'react-tabs';
import 'react-tabs/style/react-tabs.css';
import ReactConsole from 'react-console-emulator';
import apiClient from "../../utils/apiClient.js";
import 'codemirror/lib/codemirror.css';
import 'codemirror/theme/neat.css';
import 'codemirror/theme/dracula.css';
import 'codemirror/addon/edit/closebrackets';
import 'codemirror/mode/python/python';
import 'codemirror/addon/hint/show-hint';
import 'codemirror/addon/hint/show-hint.css';
import 'codemirror/mode/python/python';

import { Folder, FolderOpen, File as FileIcon, ChevronRight, ChevronDown, Plus, Trash2, Edit, MoreVertical, FileDown, FileUp } from 'lucide-react';

/**
 * Retrieves a nested object property using an array of keys as a path.
 * @param {object} obj The object to traverse.
 * @param {Array<string>} pathArray An array of keys representing the path.
 * @returns {any | undefined} The value at the specified path, or undefined if not found.
 */
const getPath = (obj, pathArray) => {
    if (!pathArray || pathArray.length === 0) return obj;
    let current = obj;
    for (const key of pathArray) {
        if (current && typeof current === 'object' && current.hasOwnProperty(key)) {
            current = current[key];
        } else {
            return undefined; // Path not found
        }
    }
    return current;
};

/**
 * Sets a nested object property using an array of keys as a path.
 * Creates intermediate objects if they don't exist.
 * @param {object} obj The object to modify.
 * @param {Array<string>} pathArray An array of keys representing the path.
 * @param {any} value The value to set.
 * @returns {object} A new object with the updated value.
 */
const setPath = (obj, pathArray, value) => {
    const newObj = JSON.parse(JSON.stringify(obj));
    let current = newObj;
    for (let i = 0; i < pathArray.length; i++) {
        const key = pathArray[i];
        if (i === pathArray.length - 1) {
            // Last key, set the value
            if (current && typeof current === 'object') {
                current[key] = value;
            }
        } else {
            // Navigate or create intermediate objects
            if (current && typeof current === 'object') {
                if (!current[key]) {
                    current[key] = {};
                }
                current = current[key];
            } else {
                // Path is invalid, cannot set
                return newObj;
            }
        }
    }
    return newObj;
};

/**
 * Deletes a nested object property using an array of keys as a path.
 * @param {object} obj The object to modify.
 * @param {Array<string>} pathArray An array of keys representing the path to delete.
 * @returns {object} A new object with the property deleted.
 */
const deletePath = (obj, pathArray) => {
    const newObj = JSON.parse(JSON.stringify(obj));
    if (!pathArray || pathArray.length === 0) return newObj;

    let current = newObj;
    let parent = null;
    const lastKeyToDelete = pathArray[pathArray.length - 1];

    // Traverse to the parent of the item to be deleted
    for (let i = 0; i < pathArray.length - 1; i++) {
        const key = pathArray[i];
        parent = current; // Keep track of parent before moving deeper
        if (current && typeof current === 'object' && current.hasOwnProperty(key)) {
            current = current[key];
        } else {
            return newObj; // Path not found
        }
    }

    // Perform deletion
    if (pathArray.length === 1) { // Deleting a root item
        if (newObj.hasOwnProperty(lastKeyToDelete)) {
            delete newObj[lastKeyToDelete];
        }
    } else if (parent && typeof parent === 'object' && parent.hasOwnProperty(lastKeyToDelete)) {
        delete parent[lastKeyToDelete];
    }
    return newObj;
};

const findPathToMainPy = (obj, currentPath = []) => {
  for (const key in obj) {
    const item = obj[key];
    const path = [...currentPath, key];

    if (key === 'main.py' && item.type === 'python') {
      return path;
    }

    if (item.type === 'folder' && item.children) {
      const result = findPathToMainPy(item.children, [...path, 'children']);
      if (result) return result;
    }
  }
  return null;
};

// =============================================================================
// INITIAL DATA
// This defines the initial structure of the files and folders in the explorer.
// =============================================================================
const initialFileStructure = {
    'main.py': {
        type: 'python',
        id: 0,
        content: `# Online Python compiler (interpreter) to run Python online.\n# Write Python 3 code in this online editor and run it.\nprint("Stem World")`
    }
};

/**
 * FileItem represents a single file or folder in the explorer.
 * It handles its own state for editing, hover, and manages user interactions
 * like selection, renaming, deletion, and drag-and-drop.
 */
const FileItem = ({ name, item, path, level, isSelected, isExpanded, onSelect, onToggle, onMove, onRename, onDelete, renamingPath, setRenamingPath }) => {
    // Determine if the item is currently in renaming mode
    const isEditing = renamingPath && JSON.stringify(renamingPath) === JSON.stringify(path);
    const [newName, setNewName] = useState(name);
    const [isHovered, setIsHovered] = useState(false);
    const inputRef = useRef(null);

    const isFolder = item.type === 'folder';
    const isProtected = item?.id === 0

    // Effect to focus and select the input field when renaming starts
    useEffect(() => {
        if (isEditing && inputRef.current) {
            inputRef.current.focus();
            inputRef.current.select();
        }
    }, [isEditing]);

    // Handler for renaming an item
    const handleRename = () => {
        if (newName && newName !== name) {
            onRename(path, newName); // Call the parent's rename handler
        }
        setRenamingPath(null); // Exit renaming mode
    };

    // Keyboard event handler for renaming input (Enter to confirm, Escape to cancel)
    const handleKeyDown = (e) => {
        if (e.key === 'Enter') handleRename();
        if (e.key === 'Escape') {
            setNewName(name); // Revert to original name
            setRenamingPath(null); // Exit renaming mode
        }
    };

    // Drag and Drop Handlers
    const handleDragStart = (e) => {
        e.stopPropagation();
        // Store the path array as a JSON string for transfer
        e.dataTransfer.setData('text/plain', JSON.stringify(path));
        const img = new Image();
        e.dataTransfer.setDragImage(img, 0, 0); // Hide default drag image for custom visual feedback
    };

    const handleDragOver = (e) => {
        e.preventDefault(); // Allow drop
        e.stopPropagation();
    };

    const handleDrop = (e) => {
        e.preventDefault();
        e.stopPropagation();
        const sourcePathArray = JSON.parse(e.dataTransfer.getData('text/plain'));

        let targetFolderPathArray; // This will be the path to the *children object* of the target folder
        if (isFolder) {
            // If dropping on a folder, the target is its 'children' object
            targetFolderPathArray = [...path, 'children']; // e.g., ['src', 'children']
        } else {
            // If dropping on a file, the target is its parent folder's 'children' object
            // Example: path = ['src', 'children', 'App.js'] -> target is ['src', 'children']
            // This ensures dropping a file onto another file moves it into the same directory.
            targetFolderPathArray = path.slice(0, path.length - 1);
        }

        // Prevent dropping an item onto itself (source path is the same as the calculated destination path of the item)
        // This handles cases like dropping 'src/App.js' back onto 'src/App.js' or 'src' folder.
        // The more complex recursive move check is handled in App.js handleMove.
        const isDroppingIntoSelfOrDescendant = targetFolderPathArray.join('/').startsWith(sourcePathArray.join('/'));

        if (isDroppingIntoSelfOrDescendant) {
          return;
        }

        onMove(sourcePathArray, targetFolderPathArray); // Pass the path to the children object
    };

    // Determines the display label for file types
    const getDisplayFileType = (fileName, itemType) => {
        if (itemType === 'python') return '[Python]';
        if (fileName.endsWith('.txt')) return '[Text]';
        if (itemType === 'file') return '[File]'; // Generic file type
        return ''; // No type label for folders
    };

    const fileTypeLabel = getDisplayFileType(name, item.type);

    // Renders the chevron icon for folders
    const renderIcon = () => {
        if (isFolder) {
            return isExpanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />;
        }
        return <span style={{ width: '16px' }}></span>; // Placeholder for alignment for files
    };

    // Renders the name or the input field if editing
    const renderName = () => {
        if (isEditing) {
            return (
                <input
                    ref={inputRef}
                    type="text"
                    value={newName}
                    onChange={(e) => setNewName(e.target.value)}
                    onBlur={handleRename} // Confirm rename on blur
                    onKeyDown={handleKeyDown}
                    className="rename-input"
                    onClick={(e) => e.stopPropagation()} // Prevent item selection when clicking input
                />
            );
        }
        return <span className="name">{name}</span>;
    };

    return (
        <div
            className={`explorer-item ${isSelected ? 'active' : ''}`}
            style={{ paddingLeft: `${level * 15 + 5}px` }}
            onClick={() => isFolder ? onToggle(path) : onSelect(path)} // Toggle folder or select file
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
            draggable={true} // Enable dragging
            onDragStart={handleDragStart}
            onDragOver={handleDragOver}
            onDrop={handleDrop}
        >
            <span className="icon-arrow">{renderIcon()}</span>
            <span className="icon-type">
                {isFolder ? (isExpanded ? <FolderOpen size={16} /> : <Folder size={16} />) : <FileIcon size={16} />}
            </span>
            {renderName()}
            {fileTypeLabel && <span className="file-type-label">{fileTypeLabel}</span>} {/* Display file type label */}
            {isHovered && !isEditing && !isProtected && (
              <div className="item-actions">
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setRenamingPath(path);
                  }}
                  title="Rename"
                >
                  <Edit size={14} />
                </button>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    onDelete(path);
                  }}
                  title="Delete"
                >
                  <Trash2 size={14} />
                </button>
              </div>
            )}
        </div>
    );
};

/**
 * FileExplorer recursively renders the file and folder structure.
 * It manages the expansion state of folders and orchestrates interactions
 * with individual FileItem components.
 */
const Explorer = ({ structure, onSelect, activePath, onMove, onRename, onDelete, onCreate, renamingPath, setRenamingPath, handleImport, handleExport }) => {
    // expandedFolders state manages which folders are open/closed.
    // It stores joined paths (e.g., 'src.children.components') for simpler state management.
    const [expandedFolders, setExpandedFolders] = useState({ 'src': true }); // 'src' folder is expanded by default
    const [dropdownOpen, setDropdownOpen] = useState(false);
    const fileInputRef = useRef(null);

    // Toggles the expanded state of a folder
    const toggleFolder = useCallback((pathArray) => {
        const pathKey = pathArray.join('.'); // Convert array path to string key
        setExpandedFolders(prev => ({ ...prev, [pathKey]: !prev[pathKey] }));
    }, []);

    // Handles dropping an item directly onto the root of the explorer
    const handleDropOnRoot = (e) => {
        e.preventDefault();
        const sourcePathArray = JSON.parse(e.dataTransfer.getData('text/plain'));
        if (sourcePathArray) {
            onMove(sourcePathArray, []); // Move to root (represented by an empty array)
        }
    };

    /**
     * Recursively renders the file and folder structure.
     * @param {object} struct The current part of the file structure to render.
     * @param {number} level The current indentation level for visual hierarchy.
     * @param {Array<string>} currentPathArray The array path leading to the current 'struct' object.
     */
    const renderStructure = (struct, level, currentPathArray) => {
        return Object.entries(struct).map(([name, item]) => {
            // itemPath is the full array path to the current file/folder item (e.g., ['src'], ['src', 'children', 'App.js'])
            const itemPath = [...currentPathArray, name];
            const isFolder = item.type === 'folder';

            // childrenPath is the array path to the 'children' object inside a folder (e.g., ['src', 'children'])
            // This is used for recursive calls to render sub-folders.
            const childrenPath = isFolder ? [...itemPath, 'children'] : null;

            return (
                <div key={itemPath.join('/')}> {/* Unique key for React list rendering */}
                    <FileItem
                        name={name}
                        item={item}
                        path={itemPath} // Pass the array path to FileItem
                        level={level}
                        isSelected={JSON.stringify(itemPath) === JSON.stringify(activePath)} // Compare array paths for selection
                        isExpanded={!!expandedFolders[itemPath.join('.')]} // Check expanded state using joined path
                        onSelect={onSelect}
                        onToggle={toggleFolder}
                        onMove={onMove}
                        onRename={onRename}
                        onDelete={onDelete}
                        renamingPath={renamingPath}
                        setRenamingPath={setRenamingPath}
                    />
                    {isFolder && expandedFolders[itemPath.join('.')] && item.children && (
                        // Recursively render children if the folder is expanded and has children
                        renderStructure(item.children, level + 1, childrenPath)
                    )}
                </div>
            );
        });
    };

    return (
        <div className="file-explorer" onDragOver={(e) => e.preventDefault()} onDrop={handleDropOnRoot}>
            <div className="sidebar-header flex">
                <span>EXPLORER</span>
                <div className="actions">
                    <button onClick={() => onCreate('file')} title="New File"><Plus size={16} /></button>
                    <button onClick={() => onCreate('folder')} title="New Folder"><Folder size={16} /></button>
                    <div className="relative">
                      <button
                        onClick={() => setDropdownOpen((prev) => !prev)}
                        title="Options"
                        className="p-1 rounded hover:bg-gray-200 dark:hover:bg-gray-700"
                      >
                        <MoreVertical size={16} />
                      </button>
                      {dropdownOpen && (
                        <div
                          className={`
                            absolute right-0 z-20 mt-2 w-40 rounded shadow-lg overflow-hidden
                            explorer-dropdown
                          `}
                        >
                          <label
                            htmlFor="file-import"
                            className="flex items-center gap-2 px-3 py-2 cursor-pointer explorer-dropdown-item transition"
                          >
                            <FileDown size={14} className="min-w-[16px]" />
                            <span className="ml-1">Import Files</span>
                            <input
                              id="file-import"
                              type="file"
                              multiple
                              onChange={(e) => {
                                handleImport(e);
                                setDropdownOpen(false);
                              }}
                              className="hidden"
                              ref={fileInputRef}
                            />
                          </label>

                          <button
                            onClick={() => {
                              handleExport();
                              setDropdownOpen(false);
                            }}
                            className="w-full text-left flex items-center gap-2 px-3 py-2 explorer-dropdown-item transition"
                            style={{ padding: "0.5rem 0.75rem"}}
                          >
                            <FileUp size={14} className="min-w-[16px]" />
                            <span className="ml-1">Export File</span>
                          </button>
                        </div>
                      )}
                    </div>
                </div>
            </div>
            {renderStructure(structure, 0, [])} {/* Start rendering from the root (empty array path) */}
        </div>
    );
};

export default function PYTHONTab({ onRegisterEditorActions }) {
  const [files, setFiles] = useState(initialFileStructure);
  const [activePath, setActivePath] = useState(["main.py"]);
  const [renamingPath, setRenamingPath] = useState(null);
  const [terminalHeight, setTerminalHeight] = useState(200);
  const [sidePanelWidth, setSidePanelWidth] = useState(200);
  const [content, setContent] = useState('');
  const [cursorPos, setCursorPos] = useState({ line: 0, ch: 0 });
  const [theme, setTheme] = useState('neat');
  const [fileTabStates, setFileTabStates] = useState({});
  const [isDraggingTerminal, setIsDraggingTerminal] = useState(false);
  const [isDraggingSidePanel, setIsDraggingSidePanel] = useState(false);
  const isDraggingTerminalRef = useRef(false);
  const isDraggingSidePanelRef = useRef(false);
  const containerRef = useRef(null);
  const dragStartY = useRef(null);
  const dragStartHeight = useRef(null);
  const dragStartX = useRef(null);
  const dragStartWidth = useRef(null);
  const cliRef = useRef(null);
  const editorRef = useRef(null);
  const outputRef = useRef();
  const logsRef = useRef();
  const [latestOutputs, setLatestOutputs] = useState({});
  const [isRunning, setIsRunning] = useState(false);
  const [inputMode, setInputMode] = useState(null);
  const [promptLabel, setPromptLabel] = useState('') 
  const [enabledModules, setEnabledModules] = useState({
    math: true,
    random: false,
    os: false,
    time: true,
    json: false,
    sprite: true,
  });

  // --- State for Delete Confirmation Modal ---
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [itemToDeletePath, setItemToDeletePath] = useState(null);
  const itemToDeleteName = useMemo(() => itemToDeletePath ? itemToDeletePath[itemToDeletePath.length - 1] : '', [itemToDeletePath]);

  /**
   * Handles selecting a file or folder in the explorer.
   * @param {Array<string>} pathArray The array path of the selected item.
   */
  const handleFileSelect = useCallback((pathArray) => {
      setRenamingPath(null); // Exit renaming mode if another item is selected
      setActivePath(pathArray);
  }, []);

  /**
   * Handles moving a file or folder via drag and drop.
   * @param {Array<string>} sourcePathArray The array path of the item being moved.
   * @param {Array<string>} destFolderPathArray The array path to the *children object* of the target folder (or empty array for root).
   */
  const handleMove = useCallback((sourcePathArray, destFolderPathArray) => {
    const sourceItem = getPath(files, sourcePathArray);
    if (!sourceItem) {
        console.error("handleMove: Source item not found.");
        return;
    }

    const sourceName = sourcePathArray[sourcePathArray.length - 1];

    let finalDestPathArray;
    if (destFolderPathArray.length === 0) {
        finalDestPathArray = [sourceName];
    } else {
        finalDestPathArray = [...destFolderPathArray, sourceName];
    }

    const isNoOpMove = JSON.stringify(sourcePathArray) === JSON.stringify(finalDestPathArray);
    if (isNoOpMove) {
        console.log("Move is a no-op (source and destination are the same).");
        return;
    }

    if (sourceItem.type === 'folder' && JSON.stringify(finalDestPathArray).startsWith(JSON.stringify(sourcePathArray))) {
        console.warn("Cannot move a folder into itself or its subfolder.");
        return;
    }

    let newFilesState = JSON.parse(JSON.stringify(files));

    const existingItemAtDest = getPath(newFilesState, finalDestPathArray);
    if (existingItemAtDest) {
        console.error("Move prevented: An item with the same name already exists at the destination.");
        return;
    }

    // --- Perform Deletion from Source ---
    let currentSourceParent = newFilesState;
    const sourceParentPath = sourcePathArray.slice(0, sourcePathArray.length - 1);
    const sourceItemName = sourcePathArray[sourcePathArray.length - 1];

    if (sourceParentPath.length > 0) {
        currentSourceParent = getPath(newFilesState, sourceParentPath);
    }

    if (currentSourceParent && typeof currentSourceParent === 'object' && currentSourceParent.hasOwnProperty(sourceItemName)) {
        delete currentSourceParent[sourceItemName];
    } else {
        console.error("Error during deletion phase of move: Item not found at parent for deletion.", { sourcePathArray, parentOfSourceItem: currentSourceParent, sourceItemName });
        return;
    }

    // --- Perform Addition to Destination ---
    let currentDestParent = newFilesState;
    const destParentPath = finalDestPathArray.slice(0, finalDestPathArray.length - 1);
    const destItemName = finalDestPathArray[finalDestPathArray.length - 1];

    if (destParentPath.length === 0) {
        currentDestParent[destItemName] = sourceItem;
    } else {
        for (let i = 0; i < destParentPath.length; i++) {
            const key = destParentPath[i];
            if (currentDestParent && typeof currentDestParent === 'object' && currentDestParent.hasOwnProperty(key)) {
                currentDestParent = currentDestParent[key];
            } else if (currentDestParent && typeof currentDestParent === 'object') {
                currentDestParent[key] = { type: 'folder', children: {} };
                currentDestParent = currentDestParent[key];
            } else {
                console.error("Error during addition phase of move: Invalid destination parent object during traversal.");
                return;
            }
        }
        if (currentDestParent && typeof currentDestParent === 'object') {
            currentDestParent[destItemName] = sourceItem;
        } else {
            console.error("Error during addition phase of move: Invalid destination parent object after traversal.");
            return;
        }
    }

    setFiles(newFilesState);

    if (activePath && JSON.stringify(activePath).startsWith(JSON.stringify(sourcePathArray))) {
        const remainingPath = activePath.slice(sourcePathArray.length);
        setActivePath([...finalDestPathArray, ...remainingPath]);
    }
  }, [activePath, files]);

  const handleTerminalInput = async (userText) => {
    try {
      // Always send raw text + newline
      const response = await apiClient.post('/python/sendInput', {
        input: userText
      });

      // Pick which terminal ref to drive
      const ref = inputMode === 'output' ? outputRef : cliRef;

      if (response.data.success) {
        // 1️⃣ We need more input
        if (response.data.inputRequest) {
          // Pull out the real prompt string
          const rawPrompt = response.data.prompt || '';
          console.log(rawPrompt)
          const cleanPrompt = rawPrompt.replace(/__INPUT_REQUIRED__/g, '');

          // Update ReactConsole’s promptLabel
          setPromptLabel(cleanPrompt);

          // Keep inputMode “open” so ReactConsole shows your prompt
          // (no manual pushToStdout here — the prompt itself is enough)
        }
        // 2️⃣ Execution has resumed or finished
        else {
          // Clear out the custom prompt
          setPromptLabel('');

          // Print only the actual output (no generic messages)
          if (response.data.output) {
            ref.current?.pushToStdout?.(response.data.output);
          }

          // And then close out input mode
          setInputMode(null);
        }
      }
      else {
        // Handle errors
        ref.current?.pushToStdout?.(`Error: ${response.data.error}\n`);
        setPromptLabel('');
        setInputMode(null);
      }
    }
    catch (e) {
      const ref = inputMode === 'output' ? outputRef : cliRef;
      ref.current?.pushToStdout?.(`Connection error: ${e.message}\n`);
      setPromptLabel('');
      setInputMode(null);
    }
  };

  const handleRename = useCallback((oldPathArray, newName) => {
    const oldItem = getPath(files, oldPathArray);
    if (!oldItem) return;

    const newPathArray = [...oldPathArray.slice(0, oldPathArray.length - 1), newName];

    if (getPath(files, newPathArray)) {
        console.error("An item with this name already exists.");
        setRenamingPath(null);
        return;
    }

    let newFilesState = deletePath(files, oldPathArray);
    if (oldItem.type !== 'folder') {
        oldItem.type = newName.endsWith('.py') ? 'python' : (newName.endsWith('.txt') ? 'text' : 'file');
    }
    newFilesState = setPath(newFilesState, newPathArray, oldItem);
    setFiles(newFilesState);

    if (activePath && JSON.stringify(activePath.slice(0, oldPathArray.length)) === JSON.stringify(oldPathArray)) {
        const remainingPath = activePath.slice(oldPathArray.length);
        setActivePath([...newPathArray, ...remainingPath]);
    }
    setRenamingPath(null);
  }, [activePath, files]);

  const requestDeleteConfirmation = useCallback((pathArray) => {
      if (JSON.stringify(pathArray) === JSON.stringify(["main.py"])) {
      alert("main.py cannot be deleted.");
      return;
    }
    setItemToDeletePath(pathArray);
    setShowDeleteConfirm(true);
  }, []);

  const handleCreate = useCallback((type) => {
    let parentPathArray = [];
    if (activePath) {
        const activeItem = getPath(files, activePath);
        if (activeItem && activeItem.type === 'folder') {
            parentPathArray = [...activePath, 'children'];
        } else if (activePath.length > 0) {
            let tempPath = [...activePath];
            while (tempPath.length > 0 && tempPath[tempPath.length - 1] !== 'children') {
                tempPath.pop();
            }
            parentPathArray = tempPath;
        }
    }

    const baseName = type === 'folder' ? 'NewFolder' : 'Untitled';
    const extension = type === 'file' ? '.py' : '';
    let newName = `${baseName}${extension}`;
    let i = 0;
    while (getPath(files, [...parentPathArray, newName])) {
        i++;
        newName = `${baseName}${i}${extension}`;
    }

    const newPathArray = [...parentPathArray, newName];
    const newItem = type === 'folder'
        ? { type: 'folder', children: {} }
        : { type: newName.endsWith('.py') ? 'python' : (newName.endsWith('.txt') ? 'text' : 'file'), content: '' };

    setFiles(prev => {
        const newState = setPath(prev, newPathArray, newItem);
        setRenamingPath(newPathArray);
        return newState;
    });
  }, [activePath, files]);

  const confirmDelete = useCallback(() => {
    if (!itemToDeletePath) return;

    let updatedFiles = files;

    // Check if main.py is inside the path being deleted
    const mainPyPath = findPathToMainPy(files);

    if (
      mainPyPath &&
      (
        JSON.stringify(itemToDeletePath) === JSON.stringify(mainPyPath.slice(0, itemToDeletePath.length)) ||
        JSON.stringify(itemToDeletePath) === JSON.stringify(mainPyPath)
      )
    ) {
      const mainPyContent = getPath(files, mainPyPath);

      // Prepare a new main.py at root
      const mainPyRootPath = ["main.py"];

      // Delete old main.py
      updatedFiles = deletePath(updatedFiles, mainPyPath);

      // Insert it at root (overwrite if needed)
      updatedFiles = setPath(updatedFiles, mainPyRootPath, mainPyContent);

      // If activePath was pointing to the old main.py, update it
      if (JSON.stringify(activePath) === JSON.stringify(mainPyPath)) {
        setActivePath(mainPyRootPath);
      }
    }

    // Proceed with normal deletion
    updatedFiles = deletePath(updatedFiles, itemToDeletePath);
    setFiles(updatedFiles);

    // Fallback: If active file got deleted, reset to main.py
    if (
      activePath &&
      (
        JSON.stringify(itemToDeletePath) === JSON.stringify(activePath) ||
        (activePath.length >= itemToDeletePath.length &&
          JSON.stringify(activePath.slice(0, itemToDeletePath.length)) === JSON.stringify(itemToDeletePath))
      )
    ) {
      setActivePath(["main.py"]);
    }

    setShowDeleteConfirm(false);
    setItemToDeletePath(null);
  }, [activePath, files, itemToDeletePath]);

  const cancelDelete = useCallback(() => {
    setShowDeleteConfirm(false);
    setItemToDeletePath(null);
  }, []);

  const formatPythonError = (rawError) => {
    if (!rawError) return '';

    // Normalize and replace file paths
    let cleaned = rawError
    // Replace any temp file reference
    .replace(/File ".*?tmp.*?\.py", /g, 'File "<exec>", ')
    // Replace standard lib paths like Python or site-packages
    .replace(/File ".*?(Lib|site-packages).*?\.py", /g, 'File "<lib>", ')
    // Replace any other local file system path
    .replace(/File ".*?\.py", /g, 'File "<user-code>", ');
    let lines = cleaned.trim().split('\n');

    // Offset line correction
    const lineMatch = cleaned.match(/line (\d+)/);
    const OFFSET = 9;
    if (lineMatch) {
      const reportedLine = parseInt(lineMatch[1], 10);
      const estimatedLine = reportedLine - OFFSET;

      if (estimatedLine > 0) {
        cleaned = cleaned.replace(/line \d+/g, `line ${estimatedLine}`);
        lines = cleaned.trim().split('\n');
      }
    }

    // Handle caret without code above
    for (let i = 1; i < lines.length; i++) {
      if (lines[i].trim() === '^' && (i === 1 || lines[i - 1].startsWith('File '))) {
        // Insert placeholder for missing code line
        lines.splice(i, 0, '    [code not shown]');
        break;
      }
    }

    return lines.join('\n');
  };

  const formatConsoleOutputBlock = (outputText) => {
    if (!outputText) return '';

    return [
      '', // padding
      outputText,
    ].join('\n');
  };
  const copyLatestTerminalOutput = () => {
    const pathKey = activePath?.join('.') || '';
    const currentTab = fileTabStates[pathKey] || 0;
    const currentOutput = latestOutputs[pathKey] || { cli: '', output: '', logs: '' };

    let textToCopy = '';
    if (currentTab === 0) textToCopy = currentOutput.cli;
    else if (currentTab === 1) textToCopy = currentOutput.output;
    else if (currentTab === 2) textToCopy = currentOutput.logs;

    if (!textToCopy) {
      return;
    }

    navigator.clipboard.writeText(textToCopy)
      .catch(() => alert('Failed to copy.'));
  };

  const clearActiveTerminal = () => {
    const pathKey = activePath?.join('.') || '';
    const currentTab = fileTabStates[pathKey] ?? 0;

    if (currentTab === 0 && cliRef.current) {
      cliRef.current.clearStdout?.();
      setTimeout(() => {
        cliRef.current.pushToStdout?.("Welcome to CLI Terminal. Type 'help' for commands.");
      }, 50);
    } else if (currentTab === 1 && outputRef.current) {
      outputRef.current.clearStdout?.();
      setTimeout(() => {
        outputRef.current.pushToStdout?.("Output Terminal Ready");
      }, 50);
    } else if (currentTab === 2 && logsRef.current) {
      logsRef.current.clearStdout?.();
      setTimeout(() => {
        logsRef.current.pushToStdout?.("Logs Terminal Ready");
      }, 50);
    }
  };

  const handleTerminalMouseDown = (e) => {
    dragStartY.current = e.clientY;
    dragStartHeight.current = terminalHeight;
    setIsDraggingTerminal(true);
  };

  const handleSidePanelMouseDown = (e) => {
    dragStartX.current = e.clientX;
    dragStartWidth.current = sidePanelWidth;
    setIsDraggingSidePanel(true);
  };

  const handleMouseMove = useCallback((e) => {
    if (isDraggingTerminalRef.current) {
      document.body.classList.add('dragging-terminal');
      const deltaY = dragStartY.current - e.clientY;
      const containerHeight = containerRef.current?.clientHeight || 600;
      const maxTerminalHeight = containerHeight - 50;
      const newHeight = Math.min(
        Math.max(dragStartHeight.current + deltaY, 0),
        maxTerminalHeight
      );
      setTerminalHeight(newHeight);
    }
    if (isDraggingSidePanelRef.current) {
      document.body.classList.add('dragging-sidepanel');
      const deltaX = e.clientX - dragStartX.current;
      const newWidth = Math.min(
        Math.max(dragStartWidth.current + deltaX, 150),
        345
      );
      setSidePanelWidth(newWidth);
    }
  }, [isDraggingTerminal, isDraggingSidePanel]);

  const handleMouseUp = useCallback(() => {
    document.body.classList.remove('dragging-terminal');
    document.body.classList.remove('dragging-sidepanel');
    setIsDraggingTerminal(false);
    setIsDraggingSidePanel(false);
  }, []);

  const handleEditorChange = (editor, data, value) => {
    setContent(value);

    const cursor = editor.getCursor();
    const cursorCoords = editor.cursorCoords(cursor, 'local');
    const scrollInfo = editor.getScrollInfo();

    const padding = 200; // adjust as needed

    const visibleLeft = scrollInfo.left;
    const visibleRight = scrollInfo.left + scrollInfo.clientWidth;

    // Horizontal scroll adjustment
    if (cursorCoords.left < visibleLeft + padding) {
      editor.scrollTo(cursorCoords.left - padding, scrollInfo.top);
    } else if (cursorCoords.right > visibleRight - padding) {
      editor.scrollTo(cursorCoords.right + padding - scrollInfo.clientWidth, scrollInfo.top);
    }

    // Vertical scroll (optional safety)
    editor.scrollIntoView({ line: cursor.line, ch: cursor.ch }, 50);
    setFiles(prev => setPath(prev, activePath, { ...getPath(prev, activePath), content: value }));
  };

  const handleImport = (event) => {
    const importedFiles = Array.from(event.target.files);

    setFiles(prev => {
      let updatedFiles = { ...prev };

      importedFiles.forEach(file => {
        const reader = new FileReader();

        reader.onload = (e) => {
          const content = e.target.result;
          const ext = file.name.split('.').pop();
          const type = ext === 'py' ? 'python' : (ext === 'txt' ? 'text' : 'file');

          const baseName = file.name.replace(/\.[^/.]+$/, '');
          let finalName = file.name;
          let counter = 1;

          while (getPath(updatedFiles, [finalName])) {
            finalName = `${baseName} (${counter}).${ext}`;
            counter++;
          }

          // Create the new file entry
          const newItem = { type, content };
          updatedFiles = setPath(updatedFiles, [finalName], newItem);

          // Since FileReader is async, we must update state inside here
          setFiles(prev => {
            let newerFiles = { ...prev };
            newerFiles = setPath(newerFiles, [finalName], newItem);
            return newerFiles;
          });
        };

        reader.readAsText(file);
      });

      return updatedFiles; // initial return in case of sync update (mostly ignored)
    });
  };

  const handleExport = () => {
    const file = getPath(files, activePath);
    if (!file) return;

    const filename = activePath?.[activePath.length - 1] || 'untitled.txt';
    const blob = new Blob([file.content], { type: 'text/plain' });

    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = filename;
    link.click();
  };

  const handleEditorMount = (editor) => {
    editorRef.current = editor;
    editor.focus();
    const cursor = editor.getCursor();
    editor.scrollIntoView({ line: cursor.line, ch: cursor.ch }, 100);
    
    if (typeof onRegisterEditorActions === 'function') {
      onRegisterEditorActions({
        undo: () => editor.undo(),
        redo: () => editor.redo(),
        cut: () => {
          const selectedText = editor.getSelection();
          if (selectedText) {
            navigator.clipboard.writeText(selectedText).then(() => {
              editor.replaceSelection(''); // Remove after cut
            });
          }
        },
        copy: () => {
          const selectedText = editor.getSelection();
          if (selectedText) {
            navigator.clipboard.writeText(selectedText);
          }
        },
        paste: () => {
          navigator.clipboard.readText().then((text) => {
            editor.replaceSelection(text);
          });
        },
        toggleTheme: () => {
          setTheme((prevTheme) => (prevTheme === 'neat' ? 'dracula' : 'neat'));
        },
      });
    }
    editor.on('cursorActivity', () => {
      const cursor = editor.getCursor();
      setCursorPos(cursor);
    });
    setCursorPos(editor.getCursor());
  }

  const handleRunCode = async (mode = "output") => {
    setIsRunning(true);
    const currentFile = getPath(files, activePath);

    if (!currentFile) {
      console.warn("No file selected to run.");
      return;
    }

    const outputTerminal = outputRef.current;
    const logsTerminal = logsRef.current;

    if (!outputTerminal || !logsTerminal) {
      console.warn("Output or logs terminal not ready");
      return;
    }

    const executionId = Date.now();

    try {
      const flattenFiles = (obj, prefix = '') => {
        let files = [];
        for (const [key, value] of Object.entries(obj)) {
          const path = prefix ? `${prefix}/${key}` : key;

          if (value.type === 'folder' && value.children) {
            files = files.concat(flattenFiles(value.children, path));
          } else if (value.type === 'file' || value.type === 'text' || value.type === 'python') {
            files.push({ name: path, content: value.content, ...value });
          }
        }
        return files;
      };
      const allowedModules = Object.entries(enabledModules)
        .filter(([_, enabled]) => enabled)
        .map(([name]) => name);

      const response = await apiClient.post('/python/runCode', {
        code: currentFile.content,
        input: '',
        executionId,
        files: flattenFiles(files),  // <-- send the entire virtual file system
        main: activePath.join('/'),  // optional: send main file path
        allowedModules,
      }, { timeout: 300000 });

      const result = response.data;

      if (result.inputRequest) {
        const prompt = result.prompt || 'Input > ';
        setInputMode('output'); // switch mode so user input gets picked up
        console.log(prompt)
        const cleanPrompt = prompt.replace(/__INPUT_REQUIRED__/g, '');

        // Update ReactConsole’s promptLabel
        setPromptLabel(cleanPrompt);
      }

      setFileTabStates(prev => ({
        ...prev,
        [activePath.join('.')]: 1  // switch to Output tab
      }));

      if (result?.output) {
        const outputText = result.output.trim();

        const isPythonError = outputText.includes('Traceback (most recent call last):') ||
                              /Error|Exception|SyntaxError|NameError|TypeError|IndentationError|ImportError/.test(outputText);

        if (isPythonError) {
          logsTerminal.pushToStdout?.(formatConsoleOutputBlock(formatPythonError(outputText)));
          setFileTabStates(prev => ({
            ...prev,
            [activePath.join('.')]: 2  // switch to Logs tab
          }));
          setLatestOutputs(prev => ({
            ...prev,
            [activePath.join('.')]: {
              ...(prev[activePath.join('.')] || {}),
              logs: formatPythonError(outputText),
            }
          }));
        } else {
          outputTerminal.pushToStdout?.(formatConsoleOutputBlock(outputText));
          setFileTabStates(prev => ({
            ...prev,
            [activePath.join('.')]: 1  // switch to Output tab
          }));
          setLatestOutputs(prev => ({
            ...prev,
            [activePath.join('.')]: {
              ...(prev[activePath.join('.')] || {}),
              output: result.output || '',
              logs: result.output || '',
            }
          }));
        }
      }

      if (result?.logs) {
        logsTerminal.pushToStdout?.(formatConsoleOutputBlock(result.logs));
        setLatestOutputs(prev => ({
          ...prev,
          [activePath.join('.')]: {
            ...(prev[activePath.join('.')] || {}),
            logs: formatPythonError(outputText),
          }
        }));
      }

      if (result?.createdFiles?.length) {
        setFiles(prev => {
          let updated = { ...prev };

          result.createdFiles.forEach(file => {
            const { path, ...meta } = file;
            const parts = path.split(path.includes('/') ? '/' : '\\'); // support both win/linux
            const fileName = parts.pop();
            const folders = parts;

            let current = updated;

            // Traverse or create folders
            for (const folder of folders) {
              if (!current[folder]) {
                current[folder] = { type: 'folder', children: {} };
              }
              current = current[folder].children;
            }

            // Derive fallback type if not already present in metadata
            const ext = fileName.split('.').pop();
            if (!meta.type) {
              meta.type = ext === 'py' ? 'python' : (ext === 'txt' ? 'text' : 'file');
            }

            // Insert file with all metadata (content, id, etc.)
            current[fileName] = { ...meta };
          });

          return updated;
        });
      }
    } catch (error) {
      console.error("Error running code:", error);
    } finally {
      setIsRunning(false);
    }
  };

  const handleStopCode = async () => {
    try {
      await apiClient.post('/python/stopExecution');
      setIsRunning(false);
      setInputMode(null);
    } catch (error) {
      console.error('Failed to stop execution:', error);
    }
  };

  const activeFile = getPath(files, activePath);
  const isPythonFile = activeFile?.type === 'python';

  useEffect(() => {
    function findFirstFilePath(obj, path = []) {
      if (!obj) return null;
      if (obj.type === 'python' || obj.type === 'text') return path;
      if (obj.children) {
        for (const key in obj.children) {
          const childPath = [...path, 'children', key];
          const result = findFirstFilePath(obj.children[key], childPath);
          if (result) return result;
        }
      }
      return null;
    }

    const firstFilePath = findFirstFilePath(files);
    if (firstFilePath && activePath.length === 0) {
      setActivePath(firstFilePath);
    }
  }, [files]);

  useEffect(() => {
    isDraggingTerminalRef.current = isDraggingTerminal;
    isDraggingSidePanelRef.current = isDraggingSidePanel;
  }, [isDraggingTerminal, isDraggingSidePanel]);

  useEffect(() => {
    if (isDraggingTerminal || isDraggingSidePanel) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
    } else {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    }
  }, [handleMouseMove, handleMouseUp, isDraggingTerminal, isDraggingSidePanel]);

  useEffect(() => {
    const activeFile = getPath(files, activePath);
    if (activeFile && activeFile.content !== undefined) {
      setContent(activeFile.content);
    }
  }, [activePath, files]);

  useEffect(() => {
    const activeFile = getPath(files, activePath);
    setContent(activeFile?.content || '');
  }, [activePath, files]);

  useEffect(() => {
    if (editorRef.current) {
      const editor = editorRef.current;
      editor.refresh(); // Forces CodeMirror to refresh its layout
      editor.focus();   // Focus the editor after load
      const cursor = editor.getCursor();
      editor.scrollIntoView({ line: cursor.line, ch: cursor.ch }, 100);
    }
  }, [])

  return (
    <>
    <div ref={containerRef} className={`w-full flex flex-col overflow-hidden ${theme === 'dracula' ? 'theme-dark' : 'theme-light'}`} style={{ height: 'calc(100vh - 134px)' }}>
      {/* Delete Confirmation Modal (conditionally rendered) */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40">
          <div className="bg-white dark:bg-[#2a2a2a] rounded-lg shadow-lg p-6 w-full max-w-sm border dark:border-[#3f3f3f]">
            <div className="text-center">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">Confirm Deletion</h2>
              <p className="text-sm text-gray-700 dark:text-gray-300 mb-4">
                Are you sure you want to delete "<span className="font-medium">{itemToDeleteName}</span>"?
              </p>
              <div className="flex justify-center gap-4">
                <button
                  onClick={confirmDelete}
                  className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded hover:bg-red-700"
                >
                  Yes, Delete
                </button>
                <button
                  onClick={cancelDelete}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-200 rounded hover:bg-gray-300 dark:bg-gray-600 dark:text-white dark:hover:bg-gray-500"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      {/* Top Section */}
      <div className="flex flex-grow overflow-hidden">
        {/* Side Tab (Imports) */}
        <div style={{ width: `${sidePanelWidth}px` }} className="side-panel-scroll explorer">
          <Tabs>
            <TabList className="flex gap-2 px-4 pt-4 sidebar-tabs">
              <Tab className="px-2 py-1 text-xs font-medium cursor-pointer rounded bg-white text-blue-500 shadow hover:bg-blue-100 focus:outline-none">Files</Tab>
              <Tab className="px-2 py-1 text-xs font-medium cursor-pointer rounded bg-white text-blue-500 shadow hover:bg-blue-100 focus:outline-none">Modules</Tab>
            </TabList>

            <TabPanel>
              <Explorer
                structure={files}
                onSelect={handleFileSelect}
                activePath={activePath}
                onMove={handleMove}
                onRename={handleRename}
                onDelete={requestDeleteConfirmation}
                onCreate={handleCreate}
                renamingPath={renamingPath}
                setRenamingPath={setRenamingPath}
                handleImport={handleImport}
                handleExport={handleExport}
              />
            </TabPanel>

            <TabPanel>
              <div className="flex flex-col gap-1">
                {Object.keys(enabledModules).map((mod) => (
                  <label key={mod} className="flex gap-2 px-[7px] my-[4px] modules-label cursor-pointer">
                    <input
                      type="checkbox"
                      className="w-3.5 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500"
                      checked={enabledModules[mod] || false}
                      onChange={(e) =>
                        setEnabledModules((prev) => ({
                          ...prev,
                          [mod]: e.target.checked,
                        }))
                      }
                    />
                    {mod}
                  </label>
                ))}
              </div>
            </TabPanel>
          </Tabs>
        </div>
        <div
          className="w-[0.25rem] cursor-col-resize group relative"
          onMouseDown={handleSidePanelMouseDown}
        >
          <div
            className={`absolute inset-0 rounded-sm transition-opacity duration-300
            bg-gradient-to-b from-gray-400/50 via-gray-500/50 to-gray-400/50 shadow-sm
            opacity-100 ${isDraggingSidePanel ? 'bg-blue-400 shadow-md' : ''}`}
          />
        </div>
        {/* Main IDE */}
        <div 
        className="flex-grow"
        style={{ 
          width: `calc(100vw - ${sidePanelWidth}px - 500px)`, 
          height: "100%"
        }}
        >
          <div className="h-full w-full flex-1 overflow-auto relative">
            <CodeMirror
              value={content}
              options={{
                mode: isPythonFile ? 'python' : null,
                theme: theme,
                lineNumbers: true,
                tabSize: 4,
                indentUnit: 4,
                indentWithTabs: false,
                autofocus: true,
                autoCloseBrackets: isPythonFile, // only for .py
                extraKeys: isPythonFile
                  ? { 'Ctrl-Space': 'autocomplete' }
                  : {}, // no hint for non-.py
              }}
              onBeforeChange={(editor, data, value) => setContent(value)}
              onChange={(editor, data, value) => handleEditorChange(editor, data, value)}
              editorDidMount={handleEditorMount}
              className="h-full w-full"
            />
            <div className="status-bar text-xs px-2 py-1 absolute bottom-0 right-0 w-full text-right">
              Line: {cursorPos.line + 1}, Column: {cursorPos.ch + 1}
            </div>
          </div>
        </div>
      </div>
      {/* Drag Handle */}
      <div 
      style={{ height: `${terminalHeight}px` }} 
      className="terminal relative bg-green-500 flex flex-col"
      >
        {/* Terminal Content */}
        <div className="flex-grow overflow-hidden">
          {/* Tabs Header */}
          <Tabs
            forceRenderTabPanel
            selectedIndex={
              typeof fileTabStates[activePath.join('.')] === 'number'
                ? fileTabStates[activePath.join('.')]
                : 0
            }
            onSelect={(index) => {
              setFileTabStates((prev) => ({
                ...prev,
                [activePath.join('.')]: index
              }));
            }}
          >
            <TabList className="flex gap-[4px] pt-2 px-2 pb-0 bg-[hsla(215,50%,90%,1)] border-t border-b border-[hsla(0,0%,0%,0.15)] border-l-0 border-r-0">
              <Tab className="px-4 py-2 text-sm font-medium focus:outline-none flex gap-[3px] cursor-pointer items-center bg-[#bbc5df] hover:bg-[#add1ff] transition-colors duration-200 border border-[hsla(0,0%,0%,0.15)] rounded-t-xl tab-element">
                <svg xmlns="http://www.w3.org/2000/svg" id="Layer_1" data-name="Layer 1" viewBox="0 0 24 24" width="20px" height="20px" className="tab-icon">
                  <path d="M19,0H5C2.243,0,0,2.243,0,5v14c0,2.757,2.243,5,5,5h14c2.757,0,5-2.243,5-5V5c0-2.757-2.243-5-5-5ZM7.293,12.705l2.583,2.583c.391,.391,.391,1.023,0,1.414-.195,.195-.451,.293-.707,.293s-.512-.098-.707-.293l-2.583-2.583c-.567-.566-.879-1.32-.879-2.122s.312-1.555,.879-2.122l2.583-2.583c.391-.391,1.023-.391,1.414,0s.391,1.023,0,1.414l-2.583,2.583c-.189,.189-.293,.44-.293,.707s.104,.519,.293,.708Zm10.828,1.419l-2.583,2.583c-.195,.195-.451,.293-.707,.293s-.512-.098-.707-.293c-.391-.391-.391-1.023,0-1.414l2.583-2.583c.188-.189,.293-.44,.293-.708s-.104-.518-.293-.707l-2.583-2.584c-.391-.391-.391-1.024,0-1.414,.391-.391,1.023-.391,1.414,0l2.583,2.583c.566,.566,.879,1.32,.879,2.121s-.312,1.555-.879,2.122Z"/>
                </svg>
                <p className="mt-[2px] text-xs text-[hsla(225,15%,40%,0.75)] tab-title">CLI</p>
              </Tab>
              <Tab className="px-4 py-2 text-sm font-medium focus:outline-none flex gap-[3px] cursor-pointer items-center bg-[#bbc5df] hover:bg-[#add1ff] transition-colors duration-200 border border-[hsla(0,0%,0%,0.15)] rounded-t-xl tab-element">
                <svg width="20px" height="20px" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" className="tab-icon">
                  <g id="SVGRepo_bgCarrier" stroke-width="0"></g>
                  <g id="SVGRepo_tracerCarrier" stroke-linecap="round" stroke-linejoin="round"></g>
                  <g id="SVGRepo_iconCarrier">
                    <g>
                      <path fill="none" d="M0 0h24v24H0z"></path>
                      <path d="M3 3h18a1 1 0 0 1 1 1v16a1 1 0 0 1-1 1H3a1 1 0 0 1-1-1V4a1 1 0 0 1 1-1zm9 12v2h6v-2h-6zm-3.586-3l-2.828 2.828L7 16.243 11.243 12 7 7.757 5.586 9.172 8.414 12z"></path>
                    </g>
                  </g>
                </svg>
                <p className="mt-[2px] text-xs text-[hsla(225,15%,40%,0.75)] tab-title">Output</p>
              </Tab>
              <Tab className="px-4 py-2 text-sm font-medium focus:outline-none flex gap-[3px] cursor-pointer items-center bg-[#bbc5df] hover:bg-[#add1ff] transition-colors duration-200 border border-[hsla(0,0%,0%,0.15)] rounded-t-xl tab-element">
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 384 512" width="20px" height="20px" className="tab-icon" >
                  <path d="M192 0c-41.8 0-77.4 26.7-90.5 64L64 64C28.7 64 0 92.7 0 128L0 448c0 35.3 28.7 64 64 64l256 0c35.3 0 64-28.7 64-64l0-320c0-35.3-28.7-64-64-64l-37.5 0C269.4 26.7 233.8 0 192 0zm0 64a32 32 0 1 1 0 64 32 32 0 1 1 0-64zM112 192l160 0c8.8 0 16 7.2 16 16s-7.2 16-16 16l-160 0c-8.8 0-16-7.2-16-16s7.2-16 16-16z"/>
                </svg>
                <p className="mt-[2px] text-xs text-[hsla(225,15%,40%,0.75)] tab-title">Logs</p>
              </Tab>
              <div
                className="absolute top-[7px] right-0 bg-[#bbc5df] m-[3px] p-[7px] px-[12px] border border-[hsla(0,0%,0%,0.15)] rounded-t-xl flex justify-end items-center gap-[10px]"
                style={{ width: '34vw', height: '6vh' }}
              >
                {(isPythonFile) && (
                  <div
                    onClick={isRunning ? handleStopCode : handleRunCode}
                    className="cursor-pointer transition-transform duration-300 hover:scale-125 hover:rotate-6 active:scale-90"
                    title={isRunning ? "Stop" : "Run"}
                  >
                    {isRunning ? (
                      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 384 512" width="20px" height="20px" fill="#8e8e8e" >
                        <path d="M0 128C0 92.7 28.7 64 64 64H320c35.3 0 64 28.7 64 64V384c0 35.3-28.7 64-64 64H64c-35.3 0-64-28.7-64-64V128z"/>
                      </svg>
                    ) : (
                      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 384 512" width="20px" height="20px" fill="#8e8e8e" >
                        <path d="M73 39c-14.8-9.1-33.4-9.4-48.5-.9S0 62.6 0 80L0 432c0 17.4 9.4 33.4 24.5 41.9s33.7 8.1 48.5-.9L361 297c14.3-8.7 23-24.2 23-41s-8.7-32.2-23-41L73 39z"/>
                      </svg>
                    )
                  }
                </div>
                )}
                <div
                  onClick={copyLatestTerminalOutput}
                  className="cursor-pointer transition-transform duration-300 hover:scale-125 hover:rotate-6 active:scale-90"
                  title="Copy Latest Output"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 448 512" width="20px" height="20px" fill="#8e8e8e">
                    <path d="M208 0L332.1 0c12.7 0 24.9 5.1 33.9 14.1l67.9 67.9c9 9 14.1 21.2 14.1 33.9L448 336c0 26.5-21.5 48-48 48l-192 0c-26.5 0-48-21.5-48-48l0-288c0-26.5 21.5-48 48-48zM48 128l80 0 0 64-64 0 0 256 192 0 0-32 64 0 0 48c0 26.5-21.5 48-48 48L48 512c-26.5 0-48-21.5-48-48L0 176c0-26.5 21.5-48 48-48z"/>
                  </svg>
                </div>
                <div
                  onClick={clearActiveTerminal}
                  className="cursor-pointer transition-transform duration-300 hover:scale-125 hover:rotate-6 active:scale-90"
                  title="Clear Terminal"
                >
                  <svg fill="#8e8e8e" width="20px" height="20px" viewBox="0 0 14 14" role="img" focusable="false" aria-hidden="true" xmlns="http://www.w3.org/2000/svg">
                    <g id="SVGRepo_bgCarrier" stroke-width="0"/>
                    <g id="SVGRepo_tracerCarrier" stroke-linecap="round" stroke-linejoin="round"/>
                    <g id="SVGRepo_iconCarrier">
                      <path d="m 1.2542986,6.7932135 c -0.26947924,0.1078 -0.33977817,0.45695 -0.1359113,0.66081 l 1.2841272,1.28413 1.7152939,-0.57176 -0.5717647,1.71529 2.9994211,2.9994205 c 0.2062102,0.20621 0.5530182,0.13357 0.6608099,-0.13591 l 2.2987751,-5.7106205 -2.5401347,-2.54013 -5.7106165,2.29877 z m 11.6344734,-5.15291 -0.529586,-0.52958 c -0.145284,-0.14762997 -0.384301,-0.14762997 -0.529585,0 l -2.3971935,2.39485 -0.9466922,-0.94669 c -0.098419,-0.10077 -0.267136,-0.0703 -0.3257184,0.0586 l -0.637377,1.37317 2.4862391,2.48624 1.373172,-0.63738 c 0.126538,-0.0586 0.157001,-0.2273 0.05858,-0.32572 l -0.946693,-0.94669 2.394851,-2.39719 c 0.147627,-0.14529 0.147627,-0.38196 0,-0.52959 z"/>
                    </g>
                  </svg>
                </div>
              </div>
            </TabList>

            <TabPanel className={`react-tabs__tab-panel ${theme === 'dracula' ? 'theme-dark' : 'theme-light'}`}>
              <div className="overflow-y-auto px-1" style={{ height: `calc(${terminalHeight}px - 42px)` }}>
                <ReactConsole
                  ref={cliRef}
                  style={{ height: '100%', overflow: 'hidden', minHeight: 'unset' }}
                  contentStyle={{ height: '100%', overflow: 'auto' }}
                  welcomeMessage="Welcome to CLI Terminal. Type 'help' for commands."
                  noDefaults={true}
                  errorText={''} 

                  // 📌 Dynamic prompt:
                  promptLabel={ promptLabel }
                  commands={inputMode === 'cli'
                    ? {}
                    : {
                      run: {
                        description: 'Run selected file',
                        fn: async () => {
                          await handleRunCode('cli');
                          return '';
                        }
                      },
                      python: {
                        description: 'Execute Python single-line',
                        usage: 'python <code>',
                        fn: async (...args) => {
                          const code = args.join(' ');
                          
                          if (!code.trim()) {
                            return 'Usage: python <code>';
                          }
                          
                          try {
                            const response = await apiClient.post('/python/runCode', {
                              code,                              // Direct code execution
                              files: [],                         // No files needed
                              input: '',
                              main: null,                        // Not needed for single commands
                              allowedModules: Object.entries(enabledModules).filter(([_, v]) => v).map(([k]) => k),
                              singleCommand: true,               // This triggers the new backend logic
                            });

                            const result = response.data;

                            if (result.inputRequest) {
                              // Handle input request
                              const promptText = result.prompt || 'Input > ';
                              const cleanPrompt = promptText.replace(/__INPUT_REQUIRED__/g, '');
                              setPromptInput(cleanPrompt);
                              setInputMode("cli");
                              return '';
                            }

                            if (result.success) {
                              return result.output || '';
                            } else {
                              return `Error: ${result.error || 'Unknown error'}`;
                            }
                          } catch (error) {
                            return `Error: ${error.message}`;
                          }
                        }
                      },
                      clear: {
                        description: 'Clear terminal and stop execution',
                        fn: async () => {
                          try {
                            // Stop any running Python process first
                            await apiClient.post('/python/stopExecution');
                            
                            // Clear the terminal
                            cliRef.current?.clearStdout?.();
                            
                            // Reset input mode
                            setInputMode(null);
                            
                            return '';
                          } catch (error) {
                            // Even if stop fails, still clear the terminal
                            cliRef.current?.clearStdout?.();
                            setInputMode(null);
                            return '';
                          }
                        }
                      },
                      help: {
                        description: 'Show available commands',
                        fn: () => {
                          return `Available commands:
                  run                    - Run the currently selected file
                  python <code>          - Execute Python code (each command runs in fresh environment)
                  clear                  - Clear terminal and stop any running execution
                  help                   - Show this help message

                Examples:
                  python print("Hello World")
                  python x = 5; print(x * 2)
                  python name = input("Enter name: "); print(f"Hello {name}")`;
                        }
                      }
                    }}

                  // in normal mode, ReactConsole handles commands itself.
                  // in input mode, intercept *every* line:
                  commandCallback={ ({ rawInput }) => {
                    if (inputMode) {
                    // During input, treat rawInput as input to backend
                      handleTerminalInput(rawInput)
                    } else {
                      // Otherwise (not in input mode), run regular CLI commands
                      cliRef.current?.pushToStdout?.(`Command '${rawInput}' not recognized\n`);
                    }
                  }}
                />
              </div>
            </TabPanel>

            <TabPanel className={`react-tabs__tab-panel ${theme === 'dracula' ? 'theme-dark' : 'theme-light'}`}>
              <div className="overflow-y-auto px-1"  style={{ height: `calc(${terminalHeight}px - 42px)`}}>
                <ReactConsole
                  ref={outputRef}
                  style={{ height: '100%', overflow: 'hidden', minHeight: 'unset' }}
                  contentStyle={{ height: '100%', overflow: 'auto' }}
                  welcomeMessage={<strong>Output Terminal Ready</strong>}

                  // show your custom prompt only in output mode
                  promptLabel={inputMode === 'output' ? promptLabel : ''}
                  noDefaults={true}
                  noAutomaticStdout={true}
                  errorText={' '}
                  readOnly={inputMode !== 'output'}
                  commands={inputMode === 'output' ? {} : undefined}
                  commandCallback={({ rawInput }) => {
                    if (inputMode === 'output') {
                      handleTerminalInput(rawInput)
                    }
                    return ''
                  }}
                />
              </div>
            </TabPanel>

            <TabPanel className={`react-tabs__tab-panel ${theme === 'dracula' ? 'theme-dark' : 'theme-light'}`}>
              <div className="overflow-y-auto px-1 logs-terminal"  style={{ height: `calc(${terminalHeight}px - 42px)`}}>
                <ReactConsole
                  style={{ height: '100%', overflow: 'hidden', minHeight: 'unset'  }}
                  contentStyle={{ height: '100%', overflow: 'auto' }}
                  ref={logsRef}
                  welcomeMessage={<span style={{ fontWeight: 'bold' }}>Logs Terminal Ready</span>}
                  commands={{}}
                  readOnly
                />
              </div>
            </TabPanel>
          </Tabs>
        </div>

        {/* Drag Handle at Bottom of Terminal */}
        <div
        className="absolute -top-[0.2rem] left-0 w-full h-[0.3rem] cursor-row-resize group"
        onMouseDown={handleTerminalMouseDown}
        >
          <div
            className={`absolute inset-0 rounded-sm transition-opacity duration-300
            bg-gradient-to-r from-gray-400/50 via-gray-500/50 to-gray-400/50 shadow-sm
            opacity-100 ${isDraggingTerminal ? 'bg-blue-400 shadow-md' : ''}`}
          />
        </div>
      </div>
    </div>
    <style jsx global>
      {`
        .side-panel-scroll {
          overflow: auto;
          scrollbar-width: thin;
          scrollbar-color: rgba(0, 0, 0, 0.2) transparent; /* light mode fallback */
        }
        .delete-confirm-overlay {
          position: fixed;
          inset: 0; /* full screen */
          display: flex;
          align-items: center;
          justify-content: center;
          background-color: #282a36; /* Optional dim background */
          z-index: 9999;
        }
        .delete-confirm-modal {
          background-color: var(--background); /* will be themed */
          padding: 20px;
          border-radius: 8px;
          z-index: 10000;
        }
        .theme-dark .side-panel-scroll {
          scrollbar-color: rgba(255, 255, 255, 0.15) transparent;
        }
        .theme-light .explorer {
          background-color: #f9fafb;
          color: #1f2937;
          border-right: 1px solid #e5e7eb;
        }
        .theme-dark .explorer {
          background-color: #1e1e1e;
          color: #f5f5f5;
          border-right: 1px solid #2c2c2c;
        }
        .theme-light .explorer .folder,
        .theme-light .explorer .file {
          color: #1f2937;
        }
        .theme-dark .explorer .folder,
        .theme-dark .explorer .file {
          color: #f5f5f5;
        }
        .theme-light .delete-confirm-modal {
          background-color: white;
          color: #1f2937;
          border: 1px solid #e5e7eb;
        }
        .theme-light .delete-confirm-modal .modal-content h2 {
          background-color: white;
          color: #1f2937;
          border: 1px solid #e5e7eb;
        }
        .theme-light .explorer-dropdown {
          background-color: #ffffff;
          color: #1f2937;
          border: 1px solid #e5e7eb;
        }
        .theme-dark .explorer-dropdown {
          background-color: #2a2a2a;
          color: #f5f5f5;
          border: 1px solid #3f3f3f;
        }
        .theme-light .explorer-dropdown-item:hover {
          background-color: #f3f4f6;
        }
        .theme-dark .explorer-dropdown-item:hover {
          background-color: #3a3a3a;
        }
        .theme-light .modules-label:hover {
          background-color: #e8eef1;
        }
        .theme-dark .modules-label:hover {
          background-color: #252525;
        }
        /* ============================
          Explorer Sidebar Panel
        ============================= */
        .theme-light .explorer {
          background-color: #f3f4f6;
          color: #1f2937;
          border-right: 1px solid #e5e7eb;
        }
        .theme-dark .explorer {
          background-color: #282a36;
          color: #f3f4f6;
          border-right: 1px solid #2c2c2c;
        }
        /* Sidebar Header */
        .theme-light .sidebar-tabs,
        .theme-light .explorer-title {
          background-color: #e5e7eb;
          color: #111827;
          padding: 8px 12px;
          font-weight: 600;
          font-size: 13px;
        }
        .theme-light .sidebar-header {
          background-color: #e5e7eb;
          color: #111827;
          padding: 8px 12px;
          font-weight: 600;
          font-size: 13px;
          border-bottom: 1px solid #d1d5db;
        }
        .theme-dark .sidebar-tabs,
        .theme-dark .explorer-title {
          background-color: #282a36;
          color: #ffffff;
          padding: 8px 12px;
          font-weight: 600;
          font-size: 13px;
        }
        .theme-dark .sidebar-header {
          background-color: #282a36;
          color: #ffffff;
          padding: 8px 12px;
          font-weight: 600;
          font-size: 13px;
          border-bottom: 1px solid #3f3f3f;
        }
        /* Sidebar Action Buttons */
        .sidebar-header .actions button {
          background: none;
          border: none;
          color: inherit;
          cursor: pointer;
          padding: 4px;
          font-size: 14px;
        }
        .sidebar-header .actions {
          display: flex;
          gap: 4px;
          margin-left: auto;
        }
        /* ============================
          Folder / File Items
        ============================= */
        .explorer-item {
          display: flex;
          align-items: center;
          padding: 4px 10px;
          font-size: 13px;
          cursor: pointer;
          user-select: none;
          transition: background 0.2s ease;
          border-radius: 4px;
        }
        .theme-light .explorer-item:hover {
          background-color: #e5e7eb;
        }
        .theme-dark .explorer-item:hover {
          background-color: #2f2f2f;
        }
        /* Active file/folder */
        .explorer-item.active {
          background-color: #bfdbfe;
          color: #1e3a8a;
        }
        .theme-dark .explorer-item.active {
          background-color: #2563eb;
          color: #ffffff;
        }
        /* Indentation for nested items */
        .indent {
          margin-left: 16px;
        }
        /* Icon styling */
        .icon-arrow {
          margin-right: 6px;
          font-size: 12px;
        }
        .icon-type {
          margin-right: 6px;
          font-size: 12px;
        }
        /* Rename input */
        .rename-input {
          font-size: 13px;
          padding: 2px 6px;
          width: 100%;
          border: 1px solid #ccc;
          border-radius: 4px;
        }
        /* File type label (optional) */
        .file-type-label {
          font-size: 11px;
          margin-left: auto;
          opacity: 0.5;
        }
        /* Item actions (delete, rename buttons) */
        .item-actions {
          display: flex;
          gap: 4px;
          margin-left: auto;
        }
        .item-actions button {
          background: none;
          border: none;
          padding: 2px;
          cursor: pointer;
          color: inherit;
          font-size: 12px;
        }
        .theme-dark .delete-confirm-modal {
          background-color: #282a36;
          color: #f5f5f5;
          border: 1px solid #262730;
        }
        .theme-dark .delete-confirm-modal .modal-content h2 {
          background-color: #282a36;
          color: #f5f5f5;
          border: 1px solid #262730;
        }
        .theme-light .delete-confirm-modal button {
          background-color: #f3f4f6;
          color: #1f2937;
        }
        .theme-dark .delete-confirm-modal button {
          background-color: #444;
          color: #f5f5f5;
        }
        .dragging-terminal {
          cursor: row-resize;
          user-select: none;
        }
        .dragging-sidepanel {
          cursor: col-resize;
          user-select: none;
        }
        .CodeMirror {
          height: 100%;
        }
        .CodeMirror-scroll {
          overflow: auto !important;
          overscroll-behavior: contain;
          -webkit-overflow-scrolling: touch;
          scrollbar-width: none;
        }
        .CodeMirror pre {
          white-space: pre;
        }
        .CodeMirror-hints {
          opacity: 0.6;
          transition: opacity 0.2s ease;
        }
        .CodeMirror-hints:focus-within {
          opacity: 1;
        }
        .CodeMirror-hint-active {
          background: #444 !important;
          color: #fff !important;
        }
        .CodeMirror-scroll {
          width:100%;
        }
        .CodeMirror-scrollbar-filler,
        .CodeMirror-gutter-filler {
          background-color: transparent;
        }
        .CodeMirror-hscrollbar, .CodeMirror-vscrollbar {
          scrollbar-width: thin;
          scrollbar-color: rgba(100, 100, 100, 0.4) transparent;
        }
        .CodeMirror-hscrollbar::-webkit-scrollbar, .CodeMirror-vscrollbar::-webkit-scrollbar {
          width: 8px;
          height: 8px;
        }
        .CodeMirror-hscrollbar::-webkit-scrollbar-thumb, CodeMirror-vscrollbar::-webkit-scrollbar-thumb {
          background-color: rgba(100, 100, 100, 0.4);
          border-radius: 4px;
          transition: background-color 0.3s ease;
        }
        .CodeMirror-hscrollbar::-webkit-scrollbar-thumb:hover, CodeMirror-vscrollbar::-webkit-scrollbar-thumb:hover {
          background-color: rgba(100, 100, 100, 0.7);
        }
        .CodeMirror-hscrollbar::-webkit-scrollbar-track, CodeMirror-vscrollbar::-webkit-scrollbar-track {
          background: transparent;
        }
        .CodeMirror-hscrollbar, CodeMirror-vscrollbar {
          overflow: auto;
          scrollbar-gutter: stable;
        }
        /* Hide scrollbar unless scrolling or hovering */
        .CodeMirror-hscrollbar::-webkit-scrollbar, CodeMirror-vscrollbar::-webkit-scrollbar {
          opacity: 0;
          transition: opacity 0.3s ease;
        }
        .CodeMirror-hscrollbar:hover::-webkit-scrollbar,
        .CodeMirror-hscrollbar:active::-webkit-scrollbar,
        .CodeMirror-vscrollbar:hover::-webkit-scrollbar,
        .CodeMirror-vscrollbar:active::-webkit-scrollbar {
          opacity: 1;
        }
        .tab-icon {
          fill: #8e8e8e;
          transition: fill 0.2s ease;
        }
        .react-tabs__tab--selected .tab-icon {
          fill: #000000;
        }
        .react-tabs__tab--selected .tab-title{
          color: #000000;
        }
        .react-tabs__tab--selected.tab-element{
          background-color: #ffffff;
        }
        .react-console-emulator,
        .react-console-emulator * {
          background-color: #f9f9f9 !important;
          color: #222222 !important;
          font-family: monospace !important;
          font-size: 14px !important;
        }
        .theme-dark [name="react-console-emulator"],
        .theme-dark [name="react-console-emulator"] * {
          background-color: #282a36 !important;
          color: #f9f9f9 !important;
          font-weight: bold;
        }
        .theme-light [name="react-console-emulator"],
        .theme-light [name="react-console-emulator"] * {
          background-color: #f9f9f9 !important;
          color: #222222 !important;
          font-weight: bold;
        }
        .theme-dark .status-bar {
          background-color: #262732;
          color: #f9f9f9;
          border-top: 1px solid #3f4451;
        }
        .theme-light .status-bar {
          background-color: #f3f4f6;
          color: #4b5563;
          border-top: 1px solid #e5e7eb;
        }

        .theme-dark .terminal {
          background-color: #282a36;
          color: #f9f9f9;
        }
        .theme-light .terminal {
          background-color: #f9f9f9;
          color: #222222;
        }

        .theme-dark .react-console-emulator,
        .theme-dark .react-console-emulator * {
          background-color: #282a36 !important;
          color: #f9f9f9 !important;
          font-weight: bold;
        }
        .theme-dark .logs-terminal *{
          background-color: #282a36 !important;
          color: #ff2020 !important;
          font-weight: bold;
        }
        .theme-light .logs-terminal * {
          background-color: #f9f9f9 !important;
          color: #fd0909ff !important;
          font-weight: bold;
        }
        .theme-light .react-console-emulator,
        .theme-light .react-console-emulator * {
          background-color: #f9f9f9 !important;
          color: #222222 !important;
          font-weight: bold;
        }
        .react-tabs__tab-panel {
          display: none;
        }
        .react-tabs__tab-panel--selected {
          display: block;
        }
        /* Apply spacing and styling to each line */
        div[name="react-console-emulator__content"] > div {
          white-space: pre-wrap;
          min-height: 8px;
        }
        .logs-terminal [name="react-console-emulator__content"] > div {
          font-style: italic;
          padding: 2px 0;
          min-height: 27px;
        }
      `}
    </style>
    </>
  );
}
